import { useState, useEffect } from 'react'
import { AccountCredentialForm } from '@/components/integrations/AccountCredentialForm'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { toast } from 'sonner'
import { INTEGRATIONS } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import * as z from 'zod'
import type { YardiBreezeMetadata } from '@/lib/api/integrations/yardi-breeze.client'

const parseYardiBreezeUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url)
    const match = urlObj.hostname.match(/^(\d+)\.yardione\.com$/)
    return match ? match[1] : null
  } catch {
    return null
  }
}

const yardiBreezeAdditionalSchema = z.object({
  yardiBreezeUrl: z
    .string()
    .min(1, 'Yardi Breeze URL is required')
    .refine(
      url => {
        try {
          new URL(url)
          return true
        } catch {
          return false
        }
      },
      { message: 'Please enter a valid URL' },
    )
    .refine(url => parseYardiBreezeUrl(url) !== null, {
      message:
        'URL must be in format: https://123456789.yardione.com (numeric ID)',
    }),
  defaultProperty: z.string().optional(),
})

export interface YardiBreezeConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function YardiBreezeConnectForm({
  isEditMode = false,
  onConnected,
}: YardiBreezeConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [existingCredential, setExistingCredential] = useState<{
    accountIdentifier: string
  } | null>(null)
  const [yardiBreezeUrl, setYardiBreezeUrl] = useState<string>('')
  const [defaultProperty, setDefaultProperty] = useState<string>('')
  const [isLoading, setIsLoading] = useState(isEditMode)

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const yardiBreeze = integrations.find(
          i =>
            i.type === INTEGRATIONS.YARDI_BREEZE.TYPE ||
            i.name === INTEGRATIONS.YARDI_BREEZE.NAME,
        )
        if (yardiBreeze?.connected && yardiBreeze.accountId) {
          setExistingCredential({ accountIdentifier: yardiBreeze.accountId })
          if (yardiBreeze.metadata?.configId) {
            setYardiBreezeUrl(
              `https://${yardiBreeze.metadata.configId}.yardione.com`,
            )
          }
          if (yardiBreeze.metadata?.defaultProperty) {
            setDefaultProperty(yardiBreeze.metadata.defaultProperty as string)
          }
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (data: {
    accountIdentifier: string
    password: string
    yardiBreezeUrl?: string
    defaultProperty?: string
  }) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    const configId = data.yardiBreezeUrl
      ? parseYardiBreezeUrl(data.yardiBreezeUrl)
      : null

    if (!configId) {
      toast.error('Invalid Yardi Breeze URL format')
      return
    }

    const metadata: YardiBreezeMetadata = {
      configId,
      ...(data.defaultProperty && { defaultProperty: data.defaultProperty }),
    }

    try {
      if (isEditMode) {
        if (isSuperAdmin) {
          await integrationsClient.yardiBreeze.updateAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.yardiBreeze.updateCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Yardi Breeze credentials updated successfully!')
      } else {
        if (isSuperAdmin) {
          await integrationsClient.yardiBreeze.createAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.yardiBreeze.createCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Yardi Breeze integration connected successfully!')
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} Yardi Breeze:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} Yardi Breeze integration`,
      )
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <AccountCredentialForm
      onSubmit={handleSubmit}
      accountIdentifierLabel="Email"
      accountIdentifierPlaceholder="your-yardi-email@example.com"
      passwordLabel="Password"
      passwordPlaceholder="Enter your password"
      submitButtonText={isEditMode ? 'Update' : 'Connect'}
      initialAccountIdentifier={existingCredential?.accountIdentifier}
      additionalSchema={yardiBreezeAdditionalSchema}
      initialValues={{ yardiBreezeUrl, defaultProperty }}
      confirmationText={
        isEditMode
          ? 'Update your credentials for Yardi Breeze'
          : 'Connect your credentials for Yardi Breeze'
      }
    >
      {({ form, isSubmitting }) => (
        <>
          <FormField
            control={form.control}
            name="yardiBreezeUrl"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Yardi Breeze URL</FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://yourcompanyid.yardione.com"
                    disabled={isSubmitting}
                    value={field.value as string}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                </FormControl>
                <FormDescription>
                  Your Yardi Breeze portal URL (e.g.,
                  https://yourcompanyid.yardione.com)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="defaultProperty"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Default property (optional)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="121 Main St, Anytown, CA"
                    disabled={isSubmitting}
                    value={field.value as string}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                </FormControl>
                <FormDescription>
                  The default property address to use for the integration
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </AccountCredentialForm>
  )
}
